/**
 * Dismissal store for feature announcements.
 *
 * Announcements get their own versioned localStorage key, separate from the
 * walkthrough store: expiry and frequency caps need timestamps, and the
 * walkthrough store is boolean. The value is a map of announcement id to the
 * ISO timestamp of the dismissal.
 */

const STORAGE_KEY = "helios:announcements:v1";
const SESSION_SHOWN_PREFIX = "helios:announcements:shown:";

export type DismissalMap = Record<string, string>;

/**
 * Reads the dismissal map. When `knownIds` is provided, ids no longer in the
 * registry are pruned (and the pruned map written back) so the key never
 * grows unbounded as announcements retire.
 */
export function readDismissals(knownIds?: ReadonlySet<string>): DismissalMap {
  let parsed: DismissalMap;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    parsed = raw ? JSON.parse(raw) : {};
    if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) parsed = {};
  } catch {
    return {};
  }

  if (!knownIds) return parsed;

  const pruned: DismissalMap = {};
  let dropped = false;
  for (const [id, dismissedAt] of Object.entries(parsed)) {
    if (knownIds.has(id) && typeof dismissedAt === "string") {
      pruned[id] = dismissedAt;
    } else {
      dropped = true;
    }
  }
  if (dropped) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(pruned));
    } catch {
      // Storage can be unavailable (private browsing); pruning just waits.
    }
  }
  return pruned;
}

export function dismissAnnouncement(id: string) {
  try {
    const next = { ...readDismissals(), [id]: new Date().toISOString() };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Storage can be unavailable (private browsing); the announcement
    // repeats on the next visit.
  }
}

/**
 * Session guard for the frequency cap: `Shown` fires (and the entrance
 * animation plays) at most once per announcement per session, so paging back
 * and forth is not re-counted or re-animated.
 */
export function announcementShownThisSession(id: string): boolean {
  try {
    return window.sessionStorage.getItem(SESSION_SHOWN_PREFIX + id) === "1";
  } catch {
    return false;
  }
}

export function markAnnouncementShown(id: string) {
  try {
    window.sessionStorage.setItem(SESSION_SHOWN_PREFIX + id, "1");
  } catch {
    // Without sessionStorage the event may repeat across page views.
  }
}
